import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CanEdit } from "@/shared/readOnlyManager";
import { connect } from "react-redux";
import { Container } from "reactstrap";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

import SelectProgram from "../components/SelectProgram";
import GoalPlans from "./components/GoalPlans";
import ModalWrapper from "./components/ModalWrapper";
import Events from "./Events";
import EventTypeAccessModal from "./Events/EventTypeAccessModal";
import Leaderboards from "./components/Leaderboards";

const GOAL_PLAN_STATUS_OPTIONS = [
  { value: "Active", label: "Active", tooltip: "Show goal plans that are currently active" },
  { value: "Future", label: "Scheduled", tooltip: "Show goal plans scheduled to start in the future" },
  { value: "Expired", label: "Expired", tooltip: "Show goal plans that have already ended" },
];

const getRefreshTarget = (modalName) => {
  if (modalName === "AddEvent" || modalName === "EditEvent") {
    return "events";
  }

  if (modalName === "AddGoalPlan" || modalName === "EditGoalPlan") {
    return "goalplans";
  }

  if (modalName === "AddLeaderboard" || modalName === "EditLeaderboard") {
    return "leaderboards";
  }

  return null;
};

const ProgramSettings = ({ auth, program, organization }) => {
  const { t } = useTranslation();
  const { hash } = useLocation();

  const [activeSection, setActiveSection] = useState("events");
  const [goalPlanStatus, setGoalPlanStatus] = useState("Active");
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);
  const [leaderboard, setLeaderboard] = useState({});
  const [reloadEvents, setReloadEvents] = useState(0);
  const [reloadGoalPlans, setReloadGoalPlans] = useState(0);
  const [reloadLeaderboards, setReloadLeaderboards] = useState(0);
  const [isEventTypeModalOpen, setEventTypeModalOpen] = useState(false);

  const sections = useMemo(() => {
    const items = [
      {
        id: "events",
        label: "Events",
        navTooltip: "View and manage reward events for this program",
        eyebrow: "Events",
        title: "Reward Events",
        description:
          "Manage active and disabled reward events, default values, and messaging for the selected program.",
        actionLabel: "Add Event",
        actionTooltip: "Create a new reward event",
        modalName: "AddEvent",
      },
    ];

    if (Number(program?.uses_goal_tracker) > 0) {
      items.push({
        id: "goalplans",
        label: "Goal Plans",
        navTooltip: "View and manage goal plans for this program",
        eyebrow: "Goals",
        title: "Goal Plans",
        description:
          "Create and manage goal plans, then switch between active, scheduled, and expired plans from one view.",
        actionLabel: "Create Goal Plan",
        actionTooltip: "Create a new goal plan",
        modalName: "AddGoalPlan",
      });
    }

    if (Number(program?.uses_leaderboards) > 0) {
      items.push({
        id: "leaderboards",
        label: "Leaderboards",
        navTooltip: "View and manage leaderboards for this program",
        eyebrow: "Leaderboards",
        title: "Leaderboards",
        description:
          "Configure leaderboard definitions and ranking rules without leaving this workspace.",
        actionLabel: "Add Leaderboard",
        actionTooltip: "Create a new leaderboard",
        modalName: "AddLeaderboard",
      });
    }

    return items;
  }, [program?.uses_goal_tracker, program?.uses_leaderboards]);

  useEffect(() => {
    const normalizedHash = hash?.replace("#", "");
    const fallbackSection = sections[0]?.id || "events";

    if (normalizedHash && sections.some((section) => section.id === normalizedHash)) {
      setActiveSection(normalizedHash);
      return;
    }

    setActiveSection((currentSection) =>
      sections.some((section) => section.id === currentSection)
        ? currentSection
        : fallbackSection
    );
  }, [hash, sections]);

  const updateHash = useCallback((sectionId) => {
    if (typeof window === "undefined") return;
    window.history.replaceState(null, "", `${window.location.pathname}#${sectionId}`);
  }, []);

  const onSelectSection = useCallback(
    (sectionId) => {
      setActiveSection(sectionId);
      updateHash(sectionId);
    },
    [updateHash]
  );

  const toggle = useCallback(
    (name = null, reload = false) => {
      if (name) {
        setModalName(name);
        setOpen(true);
        return;
      }

      if (reload) {
        const refreshTarget = getRefreshTarget(modalName);

        if (refreshTarget === "events") {
          setReloadEvents((value) => value + 1);
        }

        if (refreshTarget === "goalplans") {
          setReloadGoalPlans((value) => value + 1);
        }

        if (refreshTarget === "leaderboards") {
          setReloadLeaderboards((value) => value + 1);
        }
      }

      setOpen(false);
    },
    [modalName]
  );

  const activeSectionConfig =
    sections.find((section) => section.id === activeSection) || sections[0];

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [activeSectionConfig?.id]);

  const renderSectionContent = () => {
    switch (activeSectionConfig?.id) {
      case "goalplans":
        return (
          <GoalPlans
            program={program}
            organization={organization}
            status={goalPlanStatus}
            reload={reloadGoalPlans}
          />
        );

      case "leaderboards":
        return (
          <Leaderboards
            program={program}
            organization={organization}
            leaderboard={leaderboard}
            setLeaderboard={setLeaderboard}
            reload={reloadLeaderboards}
          />
        );

      case "events":
      default:
        return (
          <Events
            program={program}
            organization={organization}
            reload={reloadEvents}
          />
        );
    }
  };

  if (!auth || !program || !organization) {
    return (
      <div className="manager-home manager-program-settings-page">
        <Container className="manager-home__shell manager-home__content">
          <div className="manager-card manager-card--loading">{t("loading")}</div>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-home manager-program-settings-page">
      <ReactTooltip place="top" type="dark" effect="solid" className="app-tooltip--elevated" />
      <Container className="manager-home__shell manager-home__content">
        <div className="manager-program-settings">
          <section className="manager-card manager-program-settings__hero">
            <div className="manager-program-settings__hero-copy">
              <span className="manager-card__eyebrow">Reward Settings</span>
              <h1 className="manager-program-settings__title">Reward Settings</h1>
              <p className="manager-program-settings__description">
                Manage reward events, goal plans, and leaderboards for the selected
                program.
              </p>
            </div>

            <div
              className="manager-program-settings__control"
              data-tip="Switch to a different program to manage"
            >
              <span className="manager-program-settings__control-label">
                Program
              </span>
              <div className="manager-program-settings__control-body">
                <SelectProgram showRefresh={false} label={null} variant="manager-report" />
              </div>
            </div>
          </section>

          <section className="manager-card manager-program-settings__nav-card">
            <div className="manager-program-settings__nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  data-tip={section.navTooltip}
                  className={`manager-program-settings__nav-btn${
                    activeSection === section.id ? " is-active" : ""
                  }`}
                  onClick={() => onSelectSection(section.id)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </section>

          <section className="manager-card manager-program-settings__panel">
            <div className="manager-program-settings__panel-head">
              <div className="manager-program-settings__panel-main">
                <span className="manager-card__eyebrow">
                  {activeSectionConfig?.eyebrow}
                </span>
                <h2 className="manager-card__title">{activeSectionConfig?.title}</h2>
                <p className="manager-program-settings__panel-description">
                  {activeSectionConfig?.description}
                </p>
              </div>

              <div className="manager-program-settings__panel-toolbar">
                {activeSectionConfig?.id === "goalplans" && (
                  <div className="manager-program-settings__subtabs">
                    {GOAL_PLAN_STATUS_OPTIONS.map((statusOption) => (
                      <button
                        key={statusOption.value}
                        type="button"
                        data-tip={statusOption.tooltip}
                        className={`manager-program-settings__subtab${
                          goalPlanStatus === statusOption.value ? " is-active" : ""
                        }`}
                        onClick={() => setGoalPlanStatus(statusOption.value)}
                      >
                        {statusOption.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* {activeSectionConfig?.id === "events" && (
                  <button
                    type="button"
                    data-tip="Configure which event types managers can access"
                    className="manager-program-settings__action-btn"
                    onClick={() => setEventTypeModalOpen(true)}
                  >
                    Configure Event Types
                  </button>
                )} */}

                <CanEdit>
                  <button
                    type="button"
                    data-tip={activeSectionConfig?.actionTooltip}
                    className="manager-home__send-btn manager-program-settings__action"
                    onClick={() => toggle(activeSectionConfig?.modalName)}
                  >
                    {activeSectionConfig?.actionLabel || 'Add Item'}
                  </button>
                </CanEdit>
              </div>
            </div>

            <div className="manager-program-settings__panel-body">
              {renderSectionContent()}
            </div>
          </section>
        </div>
      </Container>

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        leaderboard={leaderboard}
        setLeaderboard={setLeaderboard}
      />

      <EventTypeAccessModal
        isOpen={isEventTypeModalOpen}
        toggle={(reload) => {
          setEventTypeModalOpen(false);
          if (reload) setReloadEvents((v) => v + 1);
        }}
        program={program}
        organization={organization}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    program: state.program,
    organization: state.organization,
  };
};

export default connect(mapStateToProps)(ProgramSettings);
